import {inject, Injectable} from '@angular/core';
import {AuthHttpClient} from '../httpClient/AuthHttpClient';
import {CacheService} from '../cache/cache.service';
import {catchError, map, Observable, of, tap} from 'rxjs';
import {UserInformation} from '../../models/user/UserInformation';
import {UserInformationAPI} from '../../api/models/user/UserInformationApi';
import {Tenant} from '../../models/tenant/Tenant';
import {environment} from '../../../environments/environment';
import * as Sentry from "@sentry/angular";

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private static AUTH_CACHE_KEY: string = "auth_userIsAuthenticated";
    private static USER_INFO_CACHE_KEY: string = "auth_userInformation";
    private static PERMISSION_CACHE_KEY: string = "auth_permission";

    private authHttpClient: AuthHttpClient = inject(AuthHttpClient);
    private userInformationCacheService: CacheService<UserInformation> = inject(CacheService);
    private authCacheService: CacheService<boolean> = inject(CacheService);
    private permissionCacheService: CacheService<string[]> = inject(CacheService);

    protected readonly superTenantIds: string[] = environment.clickkerAuth.superTenantIds;

    constructor() {
    }

    public checkIfUserIsAuthenticated(): Observable<boolean> {
        const isAuthenticated: boolean | undefined = this.authCacheService.getData(AuthService.AUTH_CACHE_KEY);
        if (!isAuthenticated) {
            return this.authHttpClient.get<UserInformationAPI>("/api/userSession/isUserLoggedIn").pipe(
                tap((result) => {
                    if (environment.isProduction) {
                        Sentry.setUser({id: result.id});
                        Sentry.setTag("tenant ID", result.currentTenant.id);
                    }

                    this.authCacheService.saveData(true, AuthService.AUTH_CACHE_KEY, 5);
                    this.userInformationCacheService.saveData(new UserInformation(
                        result.id,
                        result.email,
                        result.hasMultipleTenants,
                        new Tenant(result.currentTenant.id, result.currentTenant.name),
                        result.name,
                    ), AuthService.USER_INFO_CACHE_KEY)
                }),
                map(() => true)
            );
        }
        return of(isAuthenticated);
    }

    public getUserInformationFromCache(): UserInformation | undefined {
        return this.userInformationCacheService.getData(AuthService.USER_INFO_CACHE_KEY);
    }

    public getTenantIdFromCurrentUser(): string | undefined {
        const currentUserData: UserInformation | undefined = this.getUserInformationFromCache();
        return currentUserData?.currentTenant.id;
    }

    public refreshAccessToken(): Observable<void> {
        return this.authHttpClient.post("/api/userSession/refreshToken", null);
    }

    public logout() { // TODO uitlogknop en tenant switchknop
        this.authCacheService.removeData(AuthService.AUTH_CACHE_KEY);
        this.userInformationCacheService.removeData(AuthService.USER_INFO_CACHE_KEY);
        window.location.href = `${environment.clickkerAuth.api.url}/${environment.clickkerAuth.login.path}?clientId=${environment.clickkerAuth.login.clientId}&redirectUrl=${encodeURIComponent(window.location.href)}`
    }

    public hasPermission(permission: string): Observable<boolean> {
        return this.getAllPermissions().pipe(
            map(permissions => permissions.includes(permission))
        );
    }

    private getAllPermissions(): Observable<string[]> {
        const cachedPermissions: string[] | undefined = this.permissionCacheService.getData(AuthService.PERMISSION_CACHE_KEY);
        if (!cachedPermissions) {
            return this.authHttpClient.get<string[]>(`/api/userSession/getUserPermissions?clientId=${environment.clickkerAuth.login.clientId}`).pipe(
                tap(userPermissions => this.permissionCacheService.saveData(userPermissions, AuthService.PERMISSION_CACHE_KEY, 10)),
                catchError((error) => {
                    return of([])
                })
            )
        }
        return of(cachedPermissions ?? []);
    }

    public currentUserIsFromSuperTenant(): boolean {
        const userInformation: UserInformation | undefined = this.getUserInformationFromCache();
        return userInformation != null && this.superTenantIds.includes(userInformation.currentTenant.id);
    }
}
